import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type { ChatMessage, Conversation, UiState } from '@/types'
import { api } from '@/services/api'

const DEFAULT_SYSTEM_PROMPT = `你是一个有帮助的AI助手。请用简洁、专业的方式回答问题。回答要详细易懂。`

export const useChatStore = defineStore('chat', () => {
  // State
  const conversations = ref<Conversation[]>([])
  const uiState = ref<UiState>({
    isLoading: false,
    error: null,
    isSidebarOpen: window.innerWidth >= 1024,
    activeConversationId: null
  })

  // Getters
  const currentConversation = computed(() => 
    conversations.value.find(conv => conv.id === uiState.value.activeConversationId)
  )

  const currentMessages = computed(() => 
    currentConversation.value?.messages || []
  )

  const hasConversations = computed(() => 
    conversations.value.length > 0
  )

  // Actions
  function createNewConversation() {
    const newConversation: Conversation = {
      id: Date.now().toString(),
      title: '新对话',
      messages: [],
      createdAt: Date.now(),
      updatedAt: Date.now(),
      systemPrompt: DEFAULT_SYSTEM_PROMPT
    }
    conversations.value.unshift(newConversation)
    uiState.value.activeConversationId = newConversation.id
    uiState.value.error = null
  }

  function setCurrentConversation(id: string) {
    uiState.value.activeConversationId = id
    uiState.value.error = null
  }

  function updateConversationTitle(id: string, title: string) {
    const conversation = conversations.value.find(conv => conv.id === id)
    if (conversation) {
      conversation.title = title
      conversation.updatedAt = Date.now()
    }
  }

  function deleteConversation(id: string) {
    const index = conversations.value.findIndex(conv => conv.id === id)
    if (index !== -1) {
      conversations.value.splice(index, 1)
      if (uiState.value.activeConversationId === id) {
        uiState.value.activeConversationId = conversations.value[0]?.id || null
      }
    }
  }

  function clearConversations() {
    conversations.value = []
    uiState.value.activeConversationId = null
    uiState.value.error = null
  }

  async function sendUserMessage(content: string) {
    if (!content.trim() || !currentConversation.value || uiState.value.isLoading) {
      return
    }
    
    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      role: 'user',
      content: content.trim(),
      timestamp: Date.now()
    }
    
    // 更新会话
    currentConversation.value.messages.push(userMessage)
    currentConversation.value.updatedAt = Date.now()
    
    // 更新 UI 状态
    uiState.value.isLoading = true
    uiState.value.error = null

    try {
      // 发送消息并记录完整响应
      const response = await api.sendMessage(currentConversation.value.messages)
      console.log('Full API response:', response)
      
      if (response.error) {
        uiState.value.error = response.error
        return
      }

      // 确保我们有有效的响应内容
      const messageContent = response.message || '抱歉，我没有收到有效的回复'
      console.log('Processed message content:', messageContent)

      // 添加 AI 回复
      const assistantMessage: ChatMessage = {
        id: Date.now().toString(),
        role: 'assistant',
        content: messageContent,
        timestamp: Date.now()
      }
      
      currentConversation.value.messages.push(assistantMessage)
      currentConversation.value.updatedAt = Date.now()

      // 如果是新对话，更新标题
      if (currentConversation.value.messages.length === 2) {
        const title = content.slice(0, 20) + (content.length > 20 ? '...' : '')
        updateConversationTitle(currentConversation.value.id, title)
      }
    } catch (e) {
      console.error('Chat Error:', e)
      uiState.value.error = e instanceof Error ? e.message : '发送消息失败，请重试'
    } finally {
      uiState.value.isLoading = false
    }
  }

  // UI Actions
  function toggleSidebar() {
    uiState.value.isSidebarOpen = !uiState.value.isSidebarOpen
  }

  function closeSidebar() {
    uiState.value.isSidebarOpen = false
  }

  function clearError() {
    uiState.value.error = null
  }

  // 初始化
  if (!hasConversations.value) {
    createNewConversation()
  }

  // 导出状态和方法
  return {
    // State
    conversations,
    uiState,
    
    // Getters
    currentConversation,
    currentMessages,
    hasConversations,
    
    // Chat Actions
    createNewConversation,
    setCurrentConversation,
    updateConversationTitle,
    deleteConversation,
    clearConversations,
    sendUserMessage,
    
    // UI Actions
    toggleSidebar,
    closeSidebar,
    clearError
  }
}) 